import type { UserProfile, UserProfileRead } from "epsm-api-types/models";
import { HttpClient, type IHttpClient } from "@/services/HttpClient";
import type { PhoneNumber } from "@/shared/components/PhoneNumberForm/types";
import type {
	BillingPreviewResponse,
	StripeCheckoutRequestData,
	StripeUpdateData,
} from "@/shared/components/PricingTable/types";

export class UserService {
	private constructor(private httpClient: IHttpClient) {}

	private static instance: UserService;

	static create() {
		if (!UserService.instance) {
			UserService.instance = new UserService(HttpClient.create());
		}

		return UserService.instance;
	}

	async getData(): Promise<Partial<UserProfile & UserProfileRead>> {
		const response = await this.httpClient.get<{
			data: Partial<UserProfile & UserProfileRead>;
		}>("/user/profile");

		return response?.data?.data;
	}

	async updateData(data: Partial<UserProfile>) {
		await this.httpClient.patch<Partial<UserProfile>>("/user/profile", {
			data,
		});
	}

	async savePhone(data: PhoneNumber) {
		await this.httpClient.post<PhoneNumber>("/user/phone", {
			data,
		});
	}

	async validatePhone(data: PhoneNumber) {
		await this.httpClient.post<PhoneNumber>("/user/phone/validate", {
			data,
		});
	}

	async getStripeBillingUrl(returnUrl: string): Promise<string> {
		const response = await this.httpClient.get("/user/stripe/portal", {
			params: {
				return_url: returnUrl,
			},
		});

		return response?.data?.redirectUrl;
	}

	async getStripeCheckoutUrl(data: StripeCheckoutRequestData): Promise<string> {
		const response = await this.httpClient.post("/user/stripe/checkout", {
			data,
		});

		return response?.data?.redirectUrl;
	}

	async updateStripeSubscription(data: StripeUpdateData) {
		const response = await this.httpClient.put("/user/stripe/subscriptions", {
			data,
		});

		return response?.data;
	}

	async deleteStripeSubscription() {
		const response = await this.httpClient.delete("/user/stripe/subscriptions");

		return response?.data;
	}

	async getStripeBillingPreview(
		data: StripeUpdateData,
	): Promise<BillingPreviewResponse> {
		const response = await this.httpClient.put(
			"/user/stripe/subscriptions/preview",
			{
				data,
			},
		);

		return response?.data;
	}

	async getTokenForChat(): Promise<string> {
		const response = await this.httpClient.get<{ token: string }>(
			"/user/chat-token",
		);
		return response?.data.token;
	}
}
